import { useEffect, useState } from 'react';
import {
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
  useElements,
  useMultiPayment,
} from '@mul-pay/mptoken-react-js';
import { TokenResponse } from '@mul-pay/mptoken-js';

// mulpayから入力フォームの変更イベントの型が提供されていなかったので、自前で必要な項目のみ定義する
type ElementChangeEvent = {
  complete: boolean;
};

const styleObject = {
  style: {
    base: {
      fontSize: '16px',
      color: '#374151',
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, sans-serif',
      fontWeight: 400,
      lineHeight: '48px',
      padding: '0px 12px',
      border: '1px solid #D1D5DB',
      borderRadius: '10%',
      backgroundColor: '#FFFFFF',
      width: '100%',
      boxSizing: 'border-box',
      outline: 'none',
      transition:
        'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      '::placeholder': {
        color: '#9CA3AF',
        fontSize: '16px',
      },
      ':focus': {
        borderColor: '#3B82F6',
        boxShadow: '0 0 0 1px #3B82F6',
      },
      ':hover': {
        borderColor: '#9CA3AF',
      },
    },
    invalid: {
      color: '#EF4444',
      borderColor: '#EF4444',
      ':focus': {
        borderColor: '#EF4444',
        boxShadow: '0 0 0 1px #EF4444',
      },
    },
    complete: {
      color: '#10B981',
      borderColor: '#10B981',
    },
    empty: {
      color: '#9CA3AF',
    },
  },
};

type MulpayElementFormProps = {
  onChange: (tokenResponse?: TokenResponse) => void;
};

export const MulpayElementForm = ({ onChange }: MulpayElementFormProps) => {
  const elements = useElements();
  const mulpay = useMultiPayment();
  const [isCardNumberComplete, setIsCardNumberComplete] = useState(false);
  const [isExpiryComplete, setIsExpiryComplete] = useState(false);
  const [isCvcComplete, setIsCvcComplete] = useState(false);

  // クレカ情報入力フォームの各要素が、入力完了した時にtoken発行を行う
  // 入力完了状態から未完了の状態に変更されたら、tokenResponse: undefined で onChangeを呼び出し設定済みのトークンを消す
  useEffect(() => {
    if (!mulpay || !elements) return;
    const cardElement = elements.getElement('cardNumber');
    if (!cardElement) return;

    if (isCardNumberComplete && isExpiryComplete && isCvcComplete) {
      mulpay
        .getTokenThroughIframe(cardElement, undefined, { tokenNumber: '1' })
        .then((tokenResponse?: TokenResponse) => {
          onChange(tokenResponse);
        });
    } else {
      onChange();
    }
  }, [
    elements,
    mulpay,
    isCardNumberComplete,
    isExpiryComplete,
    isCvcComplete,
    onChange,
  ]);

  // クレカ入力フォームとして、カード番号、有効期限、セキュリティコードを入力させる
  // カード名義は取得しない。Stripe -> GMOへの移行の中でStripeではカード名義を取得していなかったため、GMOでもカード名義を入力させない
  // 既存の体験を変えず、StripeからGMOへ移行させたユーザーのカードの名義の再入力を避けるため
  return (
    <div className="grid w-full grid-cols-1 gap-4">
      {/* カード番号フィールド - 全幅 */}
      <div className="flex w-full flex-col gap-2">
        <label
          htmlFor="Field-numberInput"
          className="mb-1 text-sm font-medium text-gray-700"
        >
          カード番号
        </label>
        <div>
          <CardNumberElement
            id="Field-numberInput"
            options={styleObject}
            onChange={(e: ElementChangeEvent) => {
              setIsCardNumberComplete(!!e?.complete);
            }}
          />
        </div>
      </div>

      {/* 有効期限とセキュリティコード - 2列 */}
      <div className="grid w-full grid-cols-2 gap-4">
        <div className="flex w-full flex-col gap-2">
          <label
            htmlFor="Field-expiryInput"
            className="mb-1 text-sm font-medium text-gray-700"
          >
            有効期限
            <span className="absolute left-[-10000px]"> 月 / 年</span>
          </label>
          <div>
            <CardExpiryElement
              id="Field-expiryInput"
              options={styleObject}
              onChange={(e: ElementChangeEvent) => {
                setIsExpiryComplete(!!e?.complete);
              }}
            />
          </div>
        </div>

        <div className="flex w-full flex-col gap-2">
          <label
            htmlFor="Field-cvcInput"
            className="mb-1 text-sm font-medium text-gray-700"
          >
            セキュリティコード
          </label>
          <div>
            <CardCvcElement
              id="Field-cvcInput"
              options={styleObject}
              onChange={(e: ElementChangeEvent) => {
                setIsCvcComplete(!!e?.complete);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
